import React from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const QuestionCard = ({ 
  question, 
  answer, 
  onAnswerChange, 
  error,
  className = '' 
}) => {
  const handleMultipleChoiceChange = (optionValue) => {
    onAnswerChange(optionValue);
  };

  const handleSliderChange = (value) => {
    onAnswerChange(parseInt(value));
  };

  const handleCheckboxChange = (optionValue, checked) => {
    const currentAnswers = Array.isArray(answer) ? answer : [];
    if (checked) {
      onAnswerChange([...currentAnswers, optionValue]);
    } else {
      onAnswerChange(currentAnswers?.filter(val => val !== optionValue));
    }
  };

  const renderQuestionInput = () => {
    switch (question?.type) {
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question?.options?.map((option, index) => (
              <label
                key={index}
                className={`
                  flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all duration-200
                  ${answer === option?.value
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }
                `}
                onClick={() => handleMultipleChoiceChange(option?.value)}
              >
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-200
                  ${answer === option?.value
                    ? 'border-primary bg-primary' :'border-muted-foreground'
                  }
                `}>
                  {answer === option?.value && (
                    <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-foreground">{option?.label}</div>
                  {option?.description && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {option?.description}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        const currentAnswers = Array.isArray(answer) ? answer : [];
        return (
          <div className="space-y-3">
            {question?.options?.map((option, index) => (
              <div key={index} className="flex items-start space-x-3 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors duration-200">
                <Checkbox
                  checked={currentAnswers?.includes(option?.value)}
                  onChange={(e) => handleCheckboxChange(option?.value, e?.target?.checked)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <div className="font-medium text-foreground">{option?.label}</div>
                  {option?.description && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {option?.description}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-6">
            <div className="px-2">
              <div className="flex justify-between items-center mb-4">
                <span className="text-sm text-muted-foreground">{question?.minLabel || '1'}</span>
                <div className="text-2xl font-bold text-primary font-mono">
                  {answer || question?.min || 1}
                </div>
                <span className="text-sm text-muted-foreground">{question?.maxLabel || '5'}</span>
              </div>
              
              <div className="relative">
                <input
                  type="range"
                  min={question?.min || 1}
                  max={question?.max || 5}
                  step={1}
                  value={answer || question?.min || 1}
                  onChange={(e) => handleSliderChange(e?.target?.value)}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  {Array.from({ length: (question?.max || 5) - (question?.min || 1) + 1 }, (_, i) => (
                    <span key={i} className="w-4 text-center">
                      {(question?.min || 1) + i}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            {question?.description && (
              <div className="text-sm text-muted-foreground text-center px-4">
                {question?.description}
              </div>
            )}
          </div>
        );

      case 'text':
        return (
          <Input
            type="text"
            placeholder={question?.placeholder || "Enter your answer..."}
            value={answer || ''}
            onChange={(e) => onAnswerChange(e?.target?.value)}
            className="w-full"
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={`bg-card rounded-xl border border-border shadow-sm p-8 ${className}`}>
      <div className="space-y-6">
        {/* Question Header */}
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
              <Icon name="HelpCircle" size={18} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground leading-relaxed">
                {question?.text}
              </h3>
              {question?.subtitle && (
                <p className="text-muted-foreground mt-2">
                  {question?.subtitle}
                </p>
              )}
            </div>
          </div>
          
          {question?.required && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground ml-11">
              <Icon name="Asterisk" size={12} className="text-error" />
              <span>Required</span>
            </div>
          )}
        </div>

        {/* Question Input */}
        <div className="ml-11">
          {renderQuestionInput()}
        </div>

        {/* Error Message */}
        {error && (
          <div className="ml-11 flex items-center space-x-2 text-sm text-error">
            <Icon name="AlertCircle" size={16} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionCard;